import { Box, Typography, Paper } from '@mui/material';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { FormTextAreaField } from '@/shared/components/forms';

/**
 * Description section component for the offer creation form.
 * Allows users to enter a detailed description of their offer.
 *
 * Features:
 * - Required multiline description field (max 1000 characters)
 * - Character counter display
 * - Client-side validation with error messages
 * - Placeholder text with helpful examples
 * - Mobile-optimized with proper validation
 */

export function DescriptionSection() {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '16px',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TextSnippetIcon sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('publish.description.label')}
        </Typography>
      </Box>

      {/* Description Input */}
      <Box sx={{ mb: 0 }}>
        <Controller
          name="description"
          control={control}
          rules={{
            required: t('publish.description.required'),
            validate: (value: string) =>
              value?.trim().length > 0 || t('publish.description.required'),
            maxLength: {
              value: 1000,
              message: t('publish.description.maxLength'),
            },
          }}
          render={({ field }) => (
            <FormTextAreaField
              {...field}
              id="description"
              label={t('publish.description.label')}
              placeholder={t('publish.description.placeholder')}
              required
              error={!!errors.description}
              helperText={errors.description?.message as string}
              fullWidth
              minRows={4}
              maxLength={1000}
              showCounter
              data-testid="publish-description-field"
            />
          )}
        />
      </Box>
    </Paper>
  );
}
